import useSWR from 'swr';
import axiosInstance from 'utils/axiosInstance';
import { useErrorContext } from 'context/hooks/useErrorContext';
import { usePageContext } from 'context/hooks/usePageContext';
import { useTransactionContext } from 'context/hooks/useTransactionContext';
import { useTransferContext } from 'context/hooks/useTransferContext';
import { useEffect } from 'react';
import { checkResponse } from 'utils';
import { PAGE_SUB } from 'context/pageContext';
import { PaymentHandlerFactory } from 'services/paymentHandlers';
import { PAYMENT_PROVIDER } from 'types/enums';

// The transfer arrived short of the amount; the customer can pay the balance
// into the same account.
const UNDERPAID = 'UNDERPAID';

const useGetTransferStatus = (
  ceviantTransactionId: string,
  duration: number = 3000,
) => {
  const { setError: updateError } = useErrorContext();
  const { changePage, changeSub } = usePageContext();
  const { provider } = useTransactionContext();
  const { reference, virtualAccountNumber, underpayment, changeTransferDetails } =
    useTransferContext();

  const handler = PaymentHandlerFactory.transfer(provider as PAYMENT_PROVIDER);

  const fetcher = (url: string) =>
    axiosInstance.get(url).then((res) => ({
      status: handler.readStatus(res.data),
      underpayment: handler.readUnderpayment?.(res.data),
    }));

  // Nothing to poll until there is an account to pay into. A provider that
  // returned its own reference is looked up by it, others by the Ceviant id.
  const statusReference = reference || ceviantTransactionId;
  const statusUrl =
    virtualAccountNumber && statusReference
      ? handler.getStatusUrl(statusReference)
      : null;

  const { data, error, isLoading, mutate } = useSWR(statusUrl, fetcher, {
    refreshInterval: duration,
    shouldRetryOnError: false,
  });
  const status = data?.status;
  const latestUnderpayment = data?.underpayment;

  useEffect(() => {
    if (status === UNDERPAID) {
      // It stays UNDERPAID until a top-up lands, so the balance screen comes
      // back only when the balance changes. A customer who says they have paid
      // waits on Verifying instead.
      const next = latestUnderpayment ?? {};
      if (
        !underpayment ||
        underpayment.outstandingAmount !== next.outstandingAmount
      ) {
        changeTransferDetails({ underpayment: next });
        changeSub(PAGE_SUB.UNDERPAID);
      }
    } else if (status) {
      checkResponse(status, changeSub, changePage);
    }
    if (error) {
      updateError(error as Record<string, unknown>);
      changeSub(PAGE_SUB.ERROR);
    }
  }, [
    status,
    latestUnderpayment,
    underpayment,
    error,
    changeSub,
    changePage,
    changeTransferDetails,
    updateError,
  ]);

  const triggerFetch = () => mutate();

  return { status, loading: isLoading, error, triggerFetch };
};

export default useGetTransferStatus;
